// src/views/Admin/Locales/Locales.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  HiOutlineMagnifyingGlass,
  HiOutlineBuildingOffice2,
  HiOutlineMapPin,
  HiChevronRight,
  HiOutlineFunnel
} from 'react-icons/hi2';
import { GetAllLocals } from '../../../crud/management_api/locales';
import MiniLoadingSpinner from '../../../components/Spinners/MiniLoadingSpinner';
import './Locales.scss';

interface Local {
  local_id: number;
  local_name: string;
  state: boolean | number;
}

type StatusFilter = 'all' | 'active' | 'inactive';

const MAX_RETRIES = 3;
const RETRY_DELAY = 1500; // ms

const Locales: React.FC = () => {
  const navigate = useNavigate();
  const [locales, setLocales] = useState<Local[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  useEffect(() => {
    fetchLocalesWithRetry();
  }, []);

  const fetchLocales = async (): Promise<boolean> => {
    try {
      const response = await GetAllLocals();
      if (response.status === 200) {
        setLocales(response.data);
        setError(null);
        return true;
      }
      return false;
    } catch (err) {
      console.error('Error fetching locales:', err);
      return false;
    }
  };

  const fetchLocalesWithRetry = async (retryCount = 0): Promise<void> => {
    setLoading(true);
    setError(null);
    
    const success = await fetchLocales();
    
    if (!success && retryCount < MAX_RETRIES) {
      console.log(`Retry attempt ${retryCount + 1}/${MAX_RETRIES}...`);
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
      return fetchLocalesWithRetry(retryCount + 1);
    }
    
    if (!success) {
      setError('Error al cargar los locales');
    }
    
    setLoading(false);
  };

  // Helper function to check if local is active (handles both boolean and number)
  const isLocalActive = (state: boolean | number): boolean => {
    return state === true || state === 1;
  };

  const filteredLocales = useMemo(() => {
    return locales
      .filter(local => {
        // Search filter
        const matchesSearch = local.local_name.toLowerCase().includes(searchTerm.toLowerCase());
        
        // Status filter
        let matchesStatus = true;
        if (statusFilter === 'active') {
          matchesStatus = isLocalActive(local.state);
        } else if (statusFilter === 'inactive') {
          matchesStatus = !isLocalActive(local.state);
        }

        return matchesSearch && matchesStatus;
      })
      // Sort by local_id descending (highest first)
      .sort((a, b) => b.local_id - a.local_id);
  }, [locales, searchTerm, statusFilter]);

  const handleLocalClick = (local: Local) => {
    navigate(`/MedioPuntoAdmin/Locales/${local.local_id}`);
  };

  const getStatusBadge = (state: boolean | number) => {
    return isLocalActive(state) ? (
      <span className="status-badge active">Activo</span>
    ) : (
      <span className="status-badge inactive">Inactivo</span>
    );
  };

  const activeCount = locales.filter(l => isLocalActive(l.state)).length;
  const inactiveCount = locales.filter(l => !isLocalActive(l.state)).length;

  return (
    <div className="admin-locales">
      <div className="locales-header">
        <div className="header-title">
          <h1>Locales</h1>
          <p>Gestión de locales registrados en MedioPunto</p>
        </div>
      </div>

      <div className="locales-stats">
        <div className="stat-pill">
          <span className="stat-label">Total</span>
          <span className="stat-value">{locales.length}</span>
        </div>
        <div className="stat-pill active">
          <span className="stat-label">Activos</span>
          <span className="stat-value">{activeCount}</span>
        </div>
        <div className="stat-pill inactive">
          <span className="stat-label">Inactivos</span>
          <span className="stat-value">{inactiveCount}</span>
        </div>
      </div>

      <div className="locales-filters">
        <div className="search-bar">
          <HiOutlineMagnifyingGlass className="search-icon" size={20} />
          <input
            type="text"
            placeholder="Buscar por nombre..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-buttons">
          <HiOutlineFunnel className="filter-icon" size={18} />
          <button
            className={`filter-btn ${statusFilter === 'all' ? 'active' : ''}`}
            onClick={() => setStatusFilter('all')}
          >
            Todos
          </button>
          <button
            className={`filter-btn ${statusFilter === 'active' ? 'active' : ''}`}
            onClick={() => setStatusFilter('active')}
          >
            Activos
          </button>
          <button
            className={`filter-btn ${statusFilter === 'inactive' ? 'active' : ''}`}
            onClick={() => setStatusFilter('inactive')}
          >
            Inactivos
          </button>
        </div>
      </div>

      <div className="locales-content">
        {loading ? (
          <div className="loading-container">
            <MiniLoadingSpinner />
            <p>Cargando locales...</p>
          </div>
        ) : error ? (
          <div className="error-container">
            <p>{error}</p>
            <button onClick={() => fetchLocalesWithRetry()}>Reintentar</button>
          </div>
        ) : filteredLocales.length === 0 ? (
          <div className="empty-container">
            <HiOutlineBuildingOffice2 size={48} />
            <p>
              {searchTerm || statusFilter !== 'all'
                ? 'No se encontraron locales con los filtros aplicados'
                : 'No hay locales registrados'}
            </p>
          </div>
        ) : (
          <div className="locales-grid">
            {filteredLocales.map((local) => (
              <div
                key={local.local_id}
                className="local-card"
                onClick={() => handleLocalClick(local)}
              >
                <div className="local-card-header">
                  <div className="local-icon-wrapper">
                    <HiOutlineBuildingOffice2 size={24} />
                  </div>
                  {getStatusBadge(local.state)}
                </div>
                <div className="local-card-body">
                  <h3 className="local-name">{local.local_name}</h3>
                  <div className="local-info">
                    <HiOutlineMapPin size={14} />
                    <span>ID: {local.local_id}</span>
                  </div>
                </div>
                <div className="local-card-footer">
                  <span className="view-details">Ver detalles</span>
                  <HiChevronRight size={18} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Locales;
